import React, { useState, useEffect } from 'react';
import { History, Search, Calendar, Phone, Mail, Users, CheckSquare, StickyNote } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_TYPE_CONFIG = {
  task: { icon: CheckSquare, color: 'text-blue-600', bgColor: 'bg-blue-100', label: 'Task' },
  note: { icon: StickyNote, color: 'text-[#00B4D8]', bgColor: 'bg-cyan-100', label: 'Note' },
  call: { icon: Phone, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Call' },
  email: { icon: Mail, color: 'text-purple-600', bgColor: 'bg-purple-100', label: 'Email' },
  meeting: { icon: Users, color: 'text-orange-600', bgColor: 'bg-orange-100', label: 'Meeting' }
};

const Timeline = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });
  const [stats, setStats] = useState({ total: 0, by_type: { tasks: 0, notes: 0, calls: 0, emails: 0, meetings: 0 } });
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchActivities();
    fetchStats();
  }, [pagination, typeFilter]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const typeParam = typeFilter ? `&activity_type=${typeFilter}` : '';
      
      const response = await fetch(
        `${API_URL}/api/crm/timeline/activities?skip=${pagination.skip}&limit=${pagination.limit}${typeParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch timeline');

      const data = await response.json();
      setActivities(data);
    } catch (error) {
      console.error('Error fetching timeline:', error);
      toast({
        title: 'Error',
        description: 'Failed to load timeline',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/crm/timeline/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchActivities();
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/api/crm/timeline/search?q=${encodeURIComponent(searchTerm)}&limit=${pagination.limit}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to search timeline');

      const data = await response.json();
      setActivities(data);
    } catch (error) {
      console.error('Error searching timeline:', error);
      toast({
        title: 'Error',
        description: 'Failed to search timeline',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (type) => {
    const config = ACTIVITY_TYPE_CONFIG[type] || ACTIVITY_TYPE_CONFIG.note;
    const Icon = config.icon;
    return <Icon className={`h-5 w-5 ${config.color}`} />;
  };

  const getActivityColor = (type) => {
    return ACTIVITY_TYPE_CONFIG[type]?.bgColor || ACTIVITY_TYPE_CONFIG.note.bgColor;
  };

  const groupByDate = (activities) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    activities.forEach(activity => {
      const activityDate = new Date(activity.created_at);
      
      if (activityDate >= today) {
        groups.today.push(activity);
      } else if (activityDate >= yesterday) {
        groups.yesterday.push(activity);
      } else if (activityDate >= weekAgo) {
        groups.thisWeek.push(activity);
      } else {
        groups.older.push(activity);
      }
    });

    return groups;
  };

  const groupedActivities = groupByDate(activities);

  const renderActivityGroup = (title, activities) => {
    if (activities.length === 0) return null;

    return (
      <div key={title} className="mb-8">
        <h3 className="text-lg font-semibold text-[#2B2D42] mb-4 flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-[#00B4D8]" />
          {title}
        </h3>
        <div className="space-y-3">
          {activities.map((activity) => (
            <Card key={activity.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${getActivityColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-base font-semibold text-[#2B2D42]">
                            {activity.subject}
                          </h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getActivityColor(activity.type)} ${ACTIVITY_TYPE_CONFIG[activity.type]?.color || 'text-gray-600'}`}>
                            {ACTIVITY_TYPE_CONFIG[activity.type]?.label || activity.type}
                          </span>
                        </div>
                        {activity.description && (
                          <p className="text-sm text-[#6C757D] mb-2">
                            {activity.description.length > 150 
                              ? `${activity.description.substring(0, 150)}...` 
                              : activity.description}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-3 text-xs text-[#6C757D]">
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(activity.created_at)}
                          </span>
                          <span>
                            <span className="font-medium">Related to:</span> {activity.related_to_type}
                          </span>
                          {activity.assigned_to && (
                            <span>
                              <span className="font-medium">Assigned:</span> {activity.assigned_to}
                            </span>
                          )}
                          {activity.type === 'task' && activity.due_date && (
                            <span>
                              <span className="font-medium">Due:</span> {new Date(activity.due_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {activity.tags && activity.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {activity.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-0.5 text-xs bg-[#E0F2FE] text-[#00B4D8] rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <History className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Activity Timeline</h2>
              <p className="text-sm text-[#6C757D]">Track all CRM activities across entities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total Activities</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Notes</p>
              <p className="text-3xl font-bold text-[#00B4D8]">{stats.by_type.notes}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Tasks</p>
              <p className="text-3xl font-bold text-blue-600">{stats.by_type.tasks}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Calls</p>
              <p className="text-3xl font-bold text-green-600">{stats.by_type.calls}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Emails</p>
              <p className="text-3xl font-bold text-purple-600">{stats.by_type.emails}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Meetings</p>
              <p className="text-3xl font-bold text-orange-600">{stats.by_type.meetings}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative col-span-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search activities..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              />
            </div>

            {/* Activity Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Activity Types</option>
              <option value="note">Notes</option>
              <option value="task">Tasks</option>
              <option value="call">Calls</option>
              <option value="email">Emails</option>
              <option value="meeting">Meetings</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading timeline...</p>
          </CardContent>
        </Card>
      ) : activities.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {searchTerm || typeFilter ? 'No activities found matching your filters.' : 'No activities yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div>
          {renderActivityGroup('Today', groupedActivities.today)}
          {renderActivityGroup('Yesterday', groupedActivities.yesterday)}
          {renderActivityGroup('This Week', groupedActivities.thisWeek)}
          {renderActivityGroup('Older', groupedActivities.older)}
        </div>
      )}
    </div>
  );
};

export default Timeline;
